import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AccountOpening() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [bankCode, setBankCode] = useState("309");
  const [recentAccounts, setRecentAccounts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerKyc, setCustomerKyc] = useState(null);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [documentRequirements, setDocumentRequirements] = useState(null);
  const [editingKyc, setEditingKyc] = useState(false);

  const [accountForm, setAccountForm] = useState({
    fullName: "", email: "", phoneNumber: "", password: "",
    dateOfBirth: "", gender: "", bvn: "", nin: "",
    address: "", city: "", state: "", country: "Nigeria",
    nextOfKinName: "", nextOfKinPhone: "", nextOfKinRelationship: "",
    occupation: "", employerName: "", employerAddress: "",
    isAgent: false, branchId: "", accountTypeId: "", initialDeposit: 0
  });

  const [kycEditForm, setKycEditForm] = useState({
    bvn: "", nin: "", phoneNumber: "", dateOfBirth: "", gender: "",
    address: "", city: "", state: "",
    nextOfKinName: "", nextOfKinPhone: "", nextOfKinRelationship: "",
    occupation: "", employerName: "", employerAddress: ""
  });

  const [additionalAccountForm, setAdditionalAccountForm] = useState({ accountTypeId: "", branchId: "", initialDeposit: 0 });
  const [showAdditionalAccountModal, setShowAdditionalAccountModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentExpiry, setDocumentExpiry] = useState("");

  useEffect(() => {
    loadBranches();
    loadAccountTypes();
    loadBankCode();
    loadRecentAccounts();
    loadDocumentRequirements();
  }, []);

  const loadBranches = async () => {
    try { const res = await api.get("/Branch"); setBranches(res.data); } catch (err) { console.error("Failed to load branches:", err); }
  };

  const loadAccountTypes = async () => {
    try { const res = await api.get("/BankAccount/types"); setAccountTypes(res.data); } catch (err) { console.error("Failed to load account types:", err); }
  };

  const loadBankCode = async () => {
    try { const res = await api.get("/BankAccount/settings/bank-code"); setBankCode(res.data.bankCode); } catch (err) { console.error("Failed to load bank code:", err); }
  };

  const loadRecentAccounts = async () => {
    try { const res = await api.get("/AccountOpening/recent?days=30&limit=20"); setRecentAccounts(res.data.accounts || []); } catch (err) { console.error("Failed to load recent accounts:", err); }
  };

  const loadDocumentRequirements = async () => {
    try { const res = await api.get("/AccountOpening/document-requirements"); setDocumentRequirements(res.data); } catch (err) { console.error("Failed to load document requirements:", err); }
  };

  const loadCustomerAccounts = async (userId) => {
    try { const res = await api.get(`/BankAccount/customer/${userId}`); setCustomerAccounts(res.data); } catch (err) { setCustomerAccounts([]); }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 3) { alert("Please enter at least 3 characters to search"); return; }
    try {
      setLoading(true);
      const res = await api.get(`/AccountOpening/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (err) { alert("Search failed"); } finally { setLoading(false); }
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setActiveTab("kyc");
    setEditingKyc(false);
    try {
      const res = await api.get(`/AccountOpening/${customer.id}/kyc`);
      setCustomerKyc(res.data);
      if (res.data?.kyc) {
        setKycEditForm({
          bvn: res.data.kyc.bvn || "", nin: res.data.kyc.nin || "",
          phoneNumber: res.data.kyc.phoneNumber || "",
          dateOfBirth: res.data.kyc.dateOfBirth ? res.data.kyc.dateOfBirth.split('T')[0] : "",
          gender: res.data.kyc.gender || "", address: res.data.kyc.address || "",
          city: res.data.kyc.city || "", state: res.data.kyc.state || "",
          nextOfKinName: res.data.kyc.nextOfKinName || "",
          nextOfKinPhone: res.data.kyc.nextOfKinPhone || "",
          nextOfKinRelationship: res.data.kyc.nextOfKinRelationship || "",
          occupation: res.data.kyc.occupation || "",
          employerName: res.data.kyc.employerName || "",
          employerAddress: res.data.kyc.employerAddress || ""
        });
      }
      loadCustomerAccounts(customer.id);
    } catch (err) { setCustomerKyc(null); }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.fullName || !accountForm.email) { alert("Full name and email are required"); return; }
    if (!accountForm.accountTypeId) { alert("Please select an account type"); return; }
    if (!accountForm.branchId) { alert("Please select a branch"); return; }

    const selectedType = accountTypes.find(t => t.id === parseInt(accountForm.accountTypeId));
    if (selectedType && accountForm.initialDeposit < selectedType.minimumBalance) {
      alert(`Minimum initial deposit for ${selectedType.name} is ₦${selectedType.minimumBalance.toLocaleString()}`);
      return;
    }

    try {
      setLoading(true);
      const createRes = await api.post("/AccountOpening/create", {
        ...accountForm, branchId: parseInt(accountForm.branchId), dateOfBirth: accountForm.dateOfBirth || null
      });

      let accountNumber = createRes.data.accountNumber;
      if (accountTypes.length > 0) {
        try {
          const nubanRes = await api.post("/BankAccount/open", {
            userId: createRes.data.userId,
            accountTypeId: parseInt(accountForm.accountTypeId),
            branchId: parseInt(accountForm.branchId),
            initialDeposit: parseFloat(accountForm.initialDeposit) || 0
          });
          accountNumber = nubanRes.data.account.accountNumber;
        } catch (nubanErr) { console.error("NUBAN account creation failed:", nubanErr); }
      }
      
      alert(`Account created successfully!\n\nAccount Number: ${accountNumber}\nName: ${createRes.data.fullName}\nAccount Type: ${selectedType?.name || "Standard"}`);
      
      setSelectedCustomer({ id: createRes.data.userId, fullName: createRes.data.fullName, email: createRes.data.email, accountNumber });
      const kycRes = await api.get(`/AccountOpening/${createRes.data.userId}/kyc`);
      setCustomerKyc(kycRes.data);
      loadCustomerAccounts(createRes.data.userId);
      setActiveTab("kyc");
      
      setAccountForm({
        fullName: "", email: "", phoneNumber: "", password: "",
        dateOfBirth: "", gender: "", bvn: "", nin: "",
        address: "", city: "", state: "", country: "Nigeria",
        nextOfKinName: "", nextOfKinPhone: "", nextOfKinRelationship: "",
        occupation: "", employerName: "", employerAddress: "",
        isAgent: false, branchId: "", accountTypeId: "", initialDeposit: 0
      });
      loadRecentAccounts();
    } catch (err) { alert(err.response?.data?.message || "Failed to create account"); } finally { setLoading(false); }
  };

  const handleOpenAdditionalAccount = async (e) => {
    e.preventDefault();
    if (!selectedCustomer || !additionalAccountForm.accountTypeId || !additionalAccountForm.branchId) {
      alert("Please fill in all required fields"); return;
    }
    const selectedType = accountTypes.find(t => t.id === parseInt(additionalAccountForm.accountTypeId));
    if (selectedType && additionalAccountForm.initialDeposit < selectedType.minimumBalance) {
      alert(`Minimum initial deposit for ${selectedType.name} is ₦${selectedType.minimumBalance.toLocaleString()}`);
      return;
    }
    try {
      setLoading(true);
      const res = await api.post("/BankAccount/open", {
        userId: selectedCustomer.id,
        accountTypeId: parseInt(additionalAccountForm.accountTypeId),
        branchId: parseInt(additionalAccountForm.branchId),
        initialDeposit: parseFloat(additionalAccountForm.initialDeposit) || 0
      });
      alert(`Additional account opened!\n\nAccount Number: ${res.data.account.accountNumber}\nType: ${res.data.account.accountType}\nBranch: ${res.data.account.branch}`);
      setShowAdditionalAccountModal(false);
      setAdditionalAccountForm({ accountTypeId: "", branchId: "", initialDeposit: 0 });
      loadCustomerAccounts(selectedCustomer.id);
    } catch (err) { alert(err.response?.data?.message || "Failed to open additional account"); } finally { setLoading(false); }
  };

  const handleUpdateKyc = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    try {
      setLoading(true);
      await api.put(`/AccountOpening/${selectedCustomer.id}/kyc`, kycEditForm);
      alert("KYC information updated successfully!");
      const res = await api.get(`/AccountOpening/${selectedCustomer.id}/kyc`);
      setCustomerKyc(res.data);
      setEditingKyc(false);
    } catch (err) { alert(err.response?.data?.message || "Failed to update KYC"); } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedDocType) { alert("Please select a document type first"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("File size must be less than 5MB"); return; }
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) { alert("Only JPG, PNG, and PDF files are allowed"); return; }
    try {
      setUploadingDoc(true);
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.post(`/AccountOpening/${selectedCustomer.id}/documents/upload`, {
            documentType: selectedDocType, documentNumber, fileName: file.name,
            fileData: reader.result, fileSize: file.size, mimeType: file.type, expiryDate: documentExpiry || null
          });
          alert("Document uploaded successfully!");
          const res = await api.get(`/AccountOpening/${selectedCustomer.id}/kyc`);
          setCustomerKyc(res.data);
          setSelectedDocType(""); setDocumentNumber(""); setDocumentExpiry("");
        } catch (err) { alert(err.response?.data?.message || "Failed to upload document"); } finally { setUploadingDoc(false); }
      };
      reader.readAsDataURL(file);
    } catch (err) { setUploadingDoc(false); alert("Failed to process file"); }
  };

  const handleSubmitKyc = async () => {
    if (!selectedCustomer) return;
    if (!window.confirm("Submit KYC for verification?")) return;
    try {
      setLoading(true);
      await api.post(`/AccountOpening/${selectedCustomer.id}/kyc/submit`);
      alert("KYC submitted for verification successfully!");
      const res = await api.get(`/AccountOpening/${selectedCustomer.id}/kyc`);
      setCustomerKyc(res.data);
    } catch (err) { alert(err.response?.data?.message || "Failed to submit KYC"); } finally { setLoading(false); }
  };

  const getStatusBadge = (status, type = "kyc") => {
    const colors = type === "doc" 
      ? { Pending: { bg: "#fef3c7", color: "#92400e" }, Approved: { bg: "#d1fae5", color: "#065f46" }, Rejected: { bg: "#fee2e2", color: "#991b1b" } }
      : { NotStarted: { bg: "#e5e7eb", color: "#374151" }, Pending: { bg: "#fef3c7", color: "#92400e" }, InReview: { bg: "#dbeafe", color: "#1d4ed8" }, Approved: { bg: "#d1fae5", color: "#065f46" }, Verified: { bg: "#d1fae5", color: "#065f46" }, Rejected: { bg: "#fee2e2", color: "#991b1b" } };
    const style = colors[status] || { bg: "#e5e7eb", color: "#374151" };
    return <span style={{ padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "600", backgroundColor: style.bg, color: style.color }}>{status}</span>;
  };

  const getAccountNumberPreview = () => {
    if (!accountForm.accountTypeId || !accountForm.branchId) return null;
    const type = accountTypes.find(t => t.id === parseInt(accountForm.accountTypeId));
    const branch = branches.find(b => b.id === parseInt(accountForm.branchId));
    if (!type || !branch) return null;
    return `${bankCode}${type.classCode}${branch.code?.substring(0, 2) || "XX"}XX[C]`;
  };

  const nigerianStates = ["Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara"];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
        <h1 style={styles.title}>🏦 Account Opening & KYC</h1>
        <button onClick={() => navigate("/bank-accounts")} style={styles.manageBtn}>⚙️ Manage Account Types</button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button onClick={() => setActiveTab("new")} style={{ ...styles.tab, ...(activeTab === "new" ? styles.tabActive : {}) }}>➕ New Account</button>
        <button onClick={() => setActiveTab("search")} style={{ ...styles.tab, ...(activeTab === "search" ? styles.tabActive : {}) }}>🔍 Search Customer</button>
        <button onClick={() => setActiveTab("kyc")} style={{ ...styles.tab, ...(activeTab === "kyc" ? styles.tabActive : {}) }} disabled={!selectedCustomer}>📋 KYC {selectedCustomer && `(${selectedCustomer.fullName})`}</button>
        <button onClick={() => setActiveTab("recent")} style={{ ...styles.tab, ...(activeTab === "recent" ? styles.tabActive : {}) }}>📅 Recent</button>
      </div>

      {/* New Account Tab */}
      {activeTab === "new" && (
        <div style={styles.content}>
          <form onSubmit={handleCreateAccount}>
            {/* Account Type & Branch */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>🏦 Account Type & Branch (NUBAN)</h3>
              <div style={styles.nubanInfo}>
                <span style={styles.nubanLabel}>Bank Code:</span>
                <span style={styles.nubanCode}>{bankCode}</span>
              </div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Account Type *</label>
                  <select value={accountForm.accountTypeId} onChange={(e) => setAccountForm({ ...accountForm, accountTypeId: e.target.value })} style={styles.input} required>
                    <option value="">Select Account Type</option>
                    {accountTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.classCode} - {type.name} (Min: ₦{type.minimumBalance?.toLocaleString()})</option>
                    ))}
                  </select>
                  {accountForm.accountTypeId && (() => {
                    const type = accountTypes.find(t => t.id === parseInt(accountForm.accountTypeId));
                    return type ? (
                      <div style={styles.accountTypeInfo}>
                        <span>Interest: {type.interestRate}%</span>
                        <span>Daily Limit: ₦{type.dailyTransactionLimit?.toLocaleString()}</span>
                        <span>KYC Tier: {type.minimumKycTier}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
                <div style={styles.formGroup}>
                  <label>Branch *</label>
                  <select value={accountForm.branchId} onChange={(e) => setAccountForm({ ...accountForm, branchId: e.target.value })} style={styles.input} required>
                    <option value="">Select Branch</option>
                    {branches.filter(b => b.isActive).map(branch => (
                      <option key={branch.id} value={branch.id}>{branch.code} - {branch.name}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Initial Deposit (₦)</label>
                  <input type="number" value={accountForm.initialDeposit} onChange={(e) => setAccountForm({ ...accountForm, initialDeposit: parseFloat(e.target.value) || 0 })} style={styles.input} placeholder="0" />
                </div>
                <div style={styles.formGroup}>
                  <label style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "25px" }}>
                    <input type="checkbox" checked={accountForm.isAgent} onChange={(e) => setAccountForm({ ...accountForm, isAgent: e.target.checked })} />
                    Register as Agent
                  </label>
                </div>
              </div>
              {getAccountNumberPreview() && (
                <div style={styles.previewBox}>
                  <span style={styles.previewLabel}>Account Number Format:</span>
                  <span style={styles.previewNumber}>{getAccountNumberPreview()}</span>
                </div>
              )}
            </div>

            {/* Personal Information */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>👤 Personal Information</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}><label>Full Name *</label><input type="text" value={accountForm.fullName} onChange={(e) => setAccountForm({ ...accountForm, fullName: e.target.value })} style={styles.input} required placeholder="Enter full name" /></div>
                <div style={styles.formGroup}><label>Email *</label><input type="email" value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} style={styles.input} required placeholder="Enter email" /></div>
                <div style={styles.formGroup}><label>Phone Number</label><input type="tel" value={accountForm.phoneNumber} onChange={(e) => setAccountForm({ ...accountForm, phoneNumber: e.target.value })} style={styles.input} placeholder="08012345678" /></div>
                <div style={styles.formGroup}><label>Date of Birth</label><input type="date" value={accountForm.dateOfBirth} onChange={(e) => setAccountForm({ ...accountForm, dateOfBirth: e.target.value })} style={styles.input} /></div>
                <div style={styles.formGroup}><label>Gender</label><select value={accountForm.gender} onChange={(e) => setAccountForm({ ...accountForm, gender: e.target.value })} style={styles.input}><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
                <div style={styles.formGroup}><label>Password</label><input type="password" value={accountForm.password} onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} style={styles.input} placeholder="Auto-generated if blank" /></div>
              </div>
            </div>

            {/* Identity */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>🆔 Identity Verification</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}><label>BVN</label><input type="text" value={accountForm.bvn} onChange={(e) => setAccountForm({ ...accountForm, bvn: e.target.value.replace(/\D/g, '').slice(0, 11) })} style={styles.input} placeholder="11 digit BVN" maxLength="11" /></div>
                <div style={styles.formGroup}><label>NIN</label><input type="text" value={accountForm.nin} onChange={(e) => setAccountForm({ ...accountForm, nin: e.target.value.replace(/\D/g, '').slice(0, 11) })} style={styles.input} placeholder="11 digit NIN" maxLength="11" /></div>
              </div>
            </div>

            {/* Address */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📍 Address Information</h3>
              <div style={styles.formGrid}>
                <div style={{ ...styles.formGroup, gridColumn: "span 2" }}><label>Street Address</label><input type="text" value={accountForm.address} onChange={(e) => setAccountForm({ ...accountForm, address: e.target.value })} style={styles.input} placeholder="Enter street address" /></div>
                <div style={styles.formGroup}><label>City</label><input type="text" value={accountForm.city} onChange={(e) => setAccountForm({ ...accountForm, city: e.target.value })} style={styles.input} placeholder="City" /></div>
                <div style={styles.formGroup}><label>State</label><select value={accountForm.state} onChange={(e) => setAccountForm({ ...accountForm, state: e.target.value })} style={styles.input}><option value="">Select State</option>{nigerianStates.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
            </div>

            {/* Next of Kin */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>👨‍👩‍👧 Next of Kin</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}><label>Full Name</label><input type="text" value={accountForm.nextOfKinName} onChange={(e) => setAccountForm({ ...accountForm, nextOfKinName: e.target.value })} style={styles.input} placeholder="Name" /></div>
                <div style={styles.formGroup}><label>Phone</label><input type="tel" value={accountForm.nextOfKinPhone} onChange={(e) => setAccountForm({ ...accountForm, nextOfKinPhone: e.target.value })} style={styles.input} placeholder="Phone" /></div>
                <div style={styles.formGroup}><label>Relationship</label><select value={accountForm.nextOfKinRelationship} onChange={(e) => setAccountForm({ ...accountForm, nextOfKinRelationship: e.target.value })} style={styles.input}><option value="">Select</option><option value="Spouse">Spouse</option><option value="Parent">Parent</option><option value="Sibling">Sibling</option><option value="Child">Child</option><option value="Other">Other</option></select></div>
              </div>
            </div>

            {/* Employment */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>💼 Employment</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}><label>Occupation</label><input type="text" value={accountForm.occupation} onChange={(e) => setAccountForm({ ...accountForm, occupation: e.target.value })} style={styles.input} placeholder="Occupation" /></div>
                <div style={styles.formGroup}><label>Employer</label><input type="text" value={accountForm.employerName} onChange={(e) => setAccountForm({ ...accountForm, employerName: e.target.value })} style={styles.input} placeholder="Employer name" /></div>
                <div style={{ ...styles.formGroup, gridColumn: "span 2" }}><label>Employer Address</label><input type="text" value={accountForm.employerAddress} onChange={(e) => setAccountForm({ ...accountForm, employerAddress: e.target.value })} style={styles.input} placeholder="Address" /></div>
              </div>
            </div>

            <div style={styles.formFooter}>
              <button type="submit" disabled={loading} style={styles.submitBtn}>{loading ? "Creating..." : "🏦 Create Account"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Search Tab */}
      {activeTab === "search" && (
        <div style={styles.content}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔍 Search Customer</h3>
            <div style={styles.searchBox}>
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} style={{ ...styles.input, flex: 1 }} placeholder="Search by name, email, or account number..." />
              <button onClick={handleSearch} disabled={loading} style={styles.searchBtn}>{loading ? "..." : "Search"}</button>
            </div>
            {searchResults.length > 0 && (
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Email</th><th style={styles.th}>Account #</th><th style={styles.th}>Actions</th></tr></thead>
                <tbody>{searchResults.map(c => (
                  <tr key={c.id}><td style={styles.td}>{c.fullName} {c.isAgent && <span style={styles.agentBadge}>Agent</span>}</td><td style={styles.td}>{c.email}</td><td style={styles.td}>{c.accountNumber || "-"}</td><td style={styles.td}><button onClick={() => selectCustomer(c)} style={styles.smallBtn}>📋 KYC</button></td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* KYC Tab */}
      {activeTab === "kyc" && selectedCustomer && (
        <div style={styles.content}>
          <div style={styles.customerSummary}>
            <div><h2 style={{ margin: 0 }}>{selectedCustomer.fullName}</h2><p style={{ margin: "5px 0", color: "#6b7280" }}>{selectedCustomer.email}</p><p style={{ color: "#10b981", fontWeight: "600", fontSize: "18px" }}>Account: {selectedCustomer.accountNumber || "N/A"}</p></div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}><span>KYC: </span>{getStatusBadge(customerKyc?.kyc?.status || "NotStarted")}<span style={{ marginLeft: "10px" }}>Tier: {customerKyc?.kyc?.tier || 0}</span></div>
          </div>

          {/* Customer Accounts */}
          <div style={styles.card}>
            <div style={styles.cardHeader}><h3 style={styles.cardTitle}>💳 Customer Accounts</h3><button onClick={() => setShowAdditionalAccountModal(true)} style={styles.addAccountBtn}>+ Open Additional Account</button></div>
            {customerAccounts.length > 0 ? (
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Account #</th><th style={styles.th}>Type</th><th style={styles.th}>Branch</th><th style={styles.th}>Balance</th><th style={styles.th}>Status</th></tr></thead>
                <tbody>{customerAccounts.map(acc => (
                  <tr key={acc.id}>
                    <td style={styles.td}><span style={{ fontFamily: "monospace", fontWeight: "bold", color: "#1e3a5f" }}>{acc.accountNumber}</span></td>
                    <td style={styles.td}><span style={{ backgroundColor: "#1e3a5f", color: "white", padding: "2px 6px", borderRadius: "4px", fontSize: "10px", marginRight: "8px" }}>{acc.accountTypeCode}</span>{acc.accountType}</td>
                    <td style={styles.td}>{acc.branch}</td>
                    <td style={styles.td}>₦{acc.balance?.toLocaleString()}</td>
                    <td style={styles.td}>{getStatusBadge(acc.status)}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <p style={{ textAlign: "center", color: "#6b7280", padding: "20px" }}>No accounts found</p>}
          </div>

          {/* KYC Info */}
          <div style={styles.card}>
            <div style={styles.cardHeader}><h3 style={styles.cardTitle}>📝 KYC Information</h3>{!editingKyc && <button onClick={() => setEditingKyc(true)} style={styles.smallBtn}>✏️ Edit</button>}</div>
            {!editingKyc ? (
              <div style={styles.infoGrid}>
                <div><strong>BVN:</strong> {customerKyc?.kyc?.bvn || "-"}</div>
                <div><strong>NIN:</strong> {customerKyc?.kyc?.nin || "-"}</div>
                <div><strong>Phone:</strong> {customerKyc?.kyc?.phoneNumber || "-"}</div>
                <div><strong>DOB:</strong> {customerKyc?.kyc?.dateOfBirth ? new Date(customerKyc.kyc.dateOfBirth).toLocaleDateString() : "-"}</div>
                <div><strong>Gender:</strong> {customerKyc?.kyc?.gender || "-"}</div>
                <div><strong>Address:</strong> {customerKyc?.kyc?.address || "-"}</div>
                <div><strong>City:</strong> {customerKyc?.kyc?.city || "-"}</div>
                <div><strong>State:</strong> {customerKyc?.kyc?.state || "-"}</div>
              </div>
            ) : (
              <form onSubmit={handleUpdateKyc}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}><label>BVN</label><input type="text" value={kycEditForm.bvn} onChange={(e) => setKycEditForm({ ...kycEditForm, bvn: e.target.value })} style={styles.input} maxLength="11" /></div>
                  <div style={styles.formGroup}><label>NIN</label><input type="text" value={kycEditForm.nin} onChange={(e) => setKycEditForm({ ...kycEditForm, nin: e.target.value })} style={styles.input} maxLength="11" /></div>
                  <div style={styles.formGroup}><label>Phone</label><input type="tel" value={kycEditForm.phoneNumber} onChange={(e) => setKycEditForm({ ...kycEditForm, phoneNumber: e.target.value })} style={styles.input} /></div>
                  <div style={styles.formGroup}><label>DOB</label><input type="date" value={kycEditForm.dateOfBirth} onChange={(e) => setKycEditForm({ ...kycEditForm, dateOfBirth: e.target.value })} style={styles.input} /></div>
                  <div style={styles.formGroup}><label>Gender</label><select value={kycEditForm.gender} onChange={(e) => setKycEditForm({ ...kycEditForm, gender: e.target.value })} style={styles.input}><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
                  <div style={styles.formGroup}><label>Address</label><input type="text" value={kycEditForm.address} onChange={(e) => setKycEditForm({ ...kycEditForm, address: e.target.value })} style={styles.input} /></div>
                  <div style={styles.formGroup}><label>City</label><input type="text" value={kycEditForm.city} onChange={(e) => setKycEditForm({ ...kycEditForm, city: e.target.value })} style={styles.input} /></div>
                  <div style={styles.formGroup}><label>State</label><select value={kycEditForm.state} onChange={(e) => setKycEditForm({ ...kycEditForm, state: e.target.value })} style={styles.input}><option value="">Select</option>{nigerianStates.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                </div>
                <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
                  <button type="submit" disabled={loading} style={styles.submitBtn}>{loading ? "Saving..." : "💾 Save"}</button>
                  <button type="button" onClick={() => setEditingKyc(false)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            )}
          </div>

          {/* Documents */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📄 Documents</h3>
            {customerKyc?.documents?.length > 0 ? (
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Type</th><th style={styles.th}>File</th><th style={styles.th}>Status</th><th style={styles.th}>Uploaded</th></tr></thead>
                <tbody>{customerKyc.documents.map(doc => (
                  <tr key={doc.id}><td style={styles.td}>{doc.type}</td><td style={styles.td}>{doc.fileName}</td><td style={styles.td}>{getStatusBadge(doc.status, "doc")}</td><td style={styles.td}>{new Date(doc.uploadedAt).toLocaleDateString()}</td></tr>
                ))}</tbody>
              </table>
            ) : <p style={{ textAlign: "center", color: "#6b7280" }}>No documents uploaded</p>}
          </div>

          {/* Upload Document */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📤 Upload Document</h3>
            <div style={styles.uploadForm}>
              <div style={styles.formGroup}><label>Document Type *</label><select value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value)} style={styles.input}><option value="">Select Type</option>{documentRequirements?.documentTypes?.map(d => <option key={d.type} value={d.type}>{d.label}</option>)}</select></div>
              <div style={styles.formGroup}><label>Document Number</label><input type="text" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} style={styles.input} placeholder="ID number" /></div>
              <div style={styles.formGroup}><label>Expiry Date</label><input type="date" value={documentExpiry} onChange={(e) => setDocumentExpiry(e.target.value)} style={styles.input} /></div>
              <div style={styles.formGroup}><label>File *</label><input type="file" accept="image/*,application/pdf" onChange={handleFileUpload} disabled={!selectedDocType || uploadingDoc} style={styles.fileInput} /><small style={{ color: "#6b7280" }}>JPG, PNG, PDF (Max 5MB)</small></div>
            </div>
            {uploadingDoc && <p style={{ textAlign: "center", color: "#3b82f6" }}>Uploading...</p>}
          </div>

          <div style={styles.formFooter}>
            <button onClick={handleSubmitKyc} disabled={loading || customerKyc?.kyc?.status === "InReview" || customerKyc?.kyc?.status === "Verified"} style={{ ...styles.submitBtn, backgroundColor: customerKyc?.kyc?.status === "Verified" ? "#6b7280" : "#10b981" }}>
              {customerKyc?.kyc?.status === "InReview" ? "✓ Submitted" : customerKyc?.kyc?.status === "Verified" ? "✓ Verified" : "📨 Submit KYC"}
            </button>
          </div>
        </div>
      )}

      {/* Recent Tab */}
      {activeTab === "recent" && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}><h3 style={styles.cardTitle}>📅 Recent Accounts (30 Days)</h3><button onClick={loadRecentAccounts} style={styles.smallBtn}>🔄 Refresh</button></div>
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Email</th><th style={styles.th}>Account #</th><th style={styles.th}>Balance</th><th style={styles.th}>KYC</th><th style={styles.th}>Created</th><th style={styles.th}>Actions</th></tr></thead>
              <tbody>
                {recentAccounts.length === 0 ? <tr><td colSpan="7" style={{ ...styles.td, textAlign: "center" }}>No recent accounts</td></tr> : recentAccounts.map(a => (
                  <tr key={a.id}>
                    <td style={styles.td}>{a.fullName} {a.isAgent && <span style={styles.agentBadge}>Agent</span>}</td>
                    <td style={styles.td}>{a.email}</td>
                    <td style={styles.td}>{a.wallet?.accountNumber || "-"}</td>
                    <td style={styles.td}>₦{(a.wallet?.balance || 0).toLocaleString()}</td>
                    <td style={styles.td}>{getStatusBadge(a.kycStatus)}</td>
                    <td style={styles.td}>{new Date(a.createdAt).toLocaleDateString()}</td>
                    <td style={styles.td}><button onClick={() => selectCustomer(a)} style={styles.smallBtn}>📋 KYC</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Additional Account Modal */}
      {showAdditionalAccountModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAdditionalAccountModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>🏦 Open Additional Account</h3>
            <p style={{ marginBottom: "20px", color: "#6b7280" }}>For: <strong>{selectedCustomer?.fullName}</strong></p>
            <form onSubmit={handleOpenAdditionalAccount}>
              <div style={styles.formGroup}>
                <label>Account Type *</label>
                <select value={additionalAccountForm.accountTypeId} onChange={(e) => setAdditionalAccountForm({ ...additionalAccountForm, accountTypeId: e.target.value })} style={styles.input} required>
                  <option value="">Select Account Type</option>
                  {accountTypes.map(type => <option key={type.id} value={type.id}>{type.classCode} - {type.name} (Min: ₦{type.minimumBalance?.toLocaleString()})</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>Branch *</label>
                <select value={additionalAccountForm.branchId} onChange={(e) => setAdditionalAccountForm({ ...additionalAccountForm, branchId: e.target.value })} style={styles.input} required>
                  <option value="">Select Branch</option>
                  {branches.filter(b => b.isActive).map(branch => <option key={branch.id} value={branch.id}>{branch.code} - {branch.name}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>Initial Deposit (₦)</label>
                <input type="number" value={additionalAccountForm.initialDeposit} onChange={(e) => setAdditionalAccountForm({ ...additionalAccountForm, initialDeposit: parseFloat(e.target.value) || 0 })} style={styles.input} placeholder="0" />
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowAdditionalAccountModal(false)} style={styles.cancelBtn}>Cancel</button>
                <button type="submit" disabled={loading} style={styles.submitBtn}>{loading ? "Opening..." : "Open Account"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f3f4f6", padding: "20px" },
  header: { display: "flex", alignItems: "center", gap: "20px", marginBottom: "20px", flexWrap: "wrap" },
  backBtn: { padding: "10px 20px", backgroundColor: "#374151", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
  manageBtn: { marginLeft: "auto", padding: "10px 20px", backgroundColor: "#8b5cf6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
  title: { fontSize: "24px", fontWeight: "700", margin: 0 },
  tabs: { display: "flex", gap: "5px", marginBottom: "20px", backgroundColor: "white", padding: "5px", borderRadius: "12px", flexWrap: "wrap" },
  tab: { padding: "12px 20px", backgroundColor: "transparent", color: "#6b7280", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "500" },
  tabActive: { backgroundColor: "#10b981", color: "white" },
  content: { maxWidth: "1200px", margin: "0 auto" },
  card: { backgroundColor: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  cardTitle: { fontSize: "16px", fontWeight: "600", marginBottom: "15px", color: "#1f2937", borderBottom: "1px solid #e5e7eb", paddingBottom: "10px", margin: 0 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", borderBottom: "1px solid #e5e7eb", paddingBottom: "10px" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" },
  formGroup: { display: "flex", flexDirection: "column", gap: "5px" },
  input: { padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
  fileInput: { padding: "10px", border: "2px dashed #d1d5db", borderRadius: "8px", backgroundColor: "#f9fafb", cursor: "pointer" },
  formFooter: { display: "flex", justifyContent: "center", marginTop: "20px" },
  submitBtn: { padding: "15px 40px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "16px", fontWeight: "600" },
  cancelBtn: { padding: "10px 20px", backgroundColor: "#e5e7eb", color: "#374151", border: "none", borderRadius: "8px", cursor: "pointer" },
  searchBox: { display: "flex", gap: "10px", marginBottom: "20px" },
  searchBtn: { padding: "10px 25px", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "12px", backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase" },
  td: { padding: "12px", borderBottom: "1px solid #e5e7eb", fontSize: "14px" },
  smallBtn: { padding: "6px 12px", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  addAccountBtn: { padding: "8px 16px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "600" },
  agentBadge: { marginLeft: "8px", padding: "2px 8px", backgroundColor: "#dbeafe", color: "#1d4ed8", borderRadius: "4px", fontSize: "10px", fontWeight: "600" },
  customerSummary: { backgroundColor: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" },
  infoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" },
  uploadForm: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" },
  nubanInfo: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px", padding: "10px", backgroundColor: "#1e3a5f", borderRadius: "8px", color: "white" },
  nubanLabel: { fontSize: "12px", opacity: 0.8 },
  nubanCode: { fontSize: "20px", fontWeight: "bold", fontFamily: "monospace" },
  accountTypeInfo: { display: "flex", gap: "15px", marginTop: "8px", fontSize: "12px", color: "#6b7280" },
  previewBox: { marginTop: "15px", padding: "15px", backgroundColor: "#f0f9ff", borderRadius: "8px", border: "1px solid #bae6fd", display: "flex", alignItems: "center", gap: "15px" },
  previewLabel: { fontSize: "12px", color: "#0369a1" },
  previewNumber: { fontSize: "20px", fontFamily: "monospace", fontWeight: "bold", color: "#0c4a6e", letterSpacing: "2px" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: "20px" },
  modal: { backgroundColor: "white", borderRadius: "16px", padding: "25px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflow: "auto" },
  modalTitle: { fontSize: "20px", fontWeight: "600", marginBottom: "10px", color: "#1f2937" },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #e5e7eb" }
};
